/**
 * `/inventory/items` — the internal stock-item catalog for Ship-only goods (inventory-clarity P2b,
 * Phase A).
 *
 * Items the shop stocks and ships but does not produce (decals, stickers, pre-made goods). They are
 * deliberately OUTSIDE the S&S blank world: never on S&S invoices, never in purchase drafts —
 * restock is recorded by hand (Add / Count). On-hand is the derived running sum of each item's
 * movement ledger, exactly like blanks and printed transfers. Phase A is catalog + manual ledger
 * only; nothing consumes stock yet (consume-at-pack is a later, gated phase).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, PackagePlus } from 'lucide-react';
import { useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { MovementList } from './movements-dialog';

type StockItemOnHand = components['schemas']['StockItemOnHand'];

export function StockItemsPage() {
  const client = useApiClient();
  const { state } = useAuth();
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  const [createOpen, setCreateOpen] = useState(false);
  const [historySlug, setHistorySlug] = useState<string | null>(null);
  // Store the SLUG, not the row: the dialog must read the LIVE row after a save (frozen state would
  // show a stale "On hand now" right after recording a movement).
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const itemsQuery = useQuery({
    queryKey: ['stock-items'],
    queryFn: async () => {
      const { data, error } = await client.GET('/stock-items');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load stock items.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items;
    },
  });

  const all = itemsQuery.data ?? [];
  const archivedCount = all.filter((i) => i.archived).length;
  const items = showArchived ? all : all.filter((i) => !i.archived);
  const editingItem = all.find((i) => i.slug === editingSlug) ?? null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Finished goods we ship but don’t print — decals, stickers, pre-made items. Record restock
          by hand with <span className="font-medium">Restock</span> or a{' '}
          <span className="font-medium">Count</span>.
        </p>
        <Button
          size="sm"
          onClick={() => setCreateOpen(true)}
          disabled={!canWrite}
          title={canWrite ? undefined : 'Admins only'}
        >
          <PackagePlus className="size-4" />
          New item
        </Button>
      </div>

      {itemsQuery.isPending ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading stock items…
        </div>
      ) : itemsQuery.isError ? (
        <p className="py-8 text-sm text-destructive">
          {itemsQuery.error instanceof Error ? itemsQuery.error.message : 'Failed to load.'}
        </p>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          {all.length === 0 ? (
            <>
              No stock items yet. Tap <span className="font-medium">New item</span> to add the goods
              you stock and ship (e.g. “Bumper decal 4in”), then count what’s on the shelf.
              {!canWrite && ' Read-only? Ask an admin to add items.'}
            </>
          ) : (
            'All items are archived — tap "Show archived" to see them.'
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-xs text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">Item</th>
                <th className="px-3 py-2 text-right font-medium">On hand</th>
                <th className="px-3 py-2 text-right font-medium">Orders need</th>
                <th className="px-3 py-2 text-right font-medium">Reorder level</th>
                <th className="px-3 py-2 text-right font-medium">Last count</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const low = !i.archived && i.reorderLevel != null && i.onHand < i.reorderLevel;
                // Open Ship-only orders need more than the shelf holds — the sharper signal of the
                // two (it's real demand, not a threshold), so it wins the badge slot.
                const short = !i.archived && i.openDemand != null && i.onHand < i.openDemand;
                return (
                  <tr key={i.slug} className="border-t">
                    <td className="px-3 py-2">
                      <span className="font-medium">{i.name}</span>
                      {i.archived && (
                        <Badge variant="outline" className="ml-2 text-muted-foreground">
                          Archived
                        </Badge>
                      )}
                      {short ? (
                        <Badge
                          variant="outline"
                          className="ml-2 border-red-300 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
                        >
                          Short for open orders
                        </Badge>
                      ) : (
                        low && (
                          <Badge
                            variant="outline"
                            className="ml-2 border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
                          >
                            Below reorder level
                          </Badge>
                        )
                      )}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-2 text-right tabular-nums',
                        i.onHand < 0 && 'font-semibold text-red-700 dark:text-red-400',
                      )}
                    >
                      {i.onHand}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-2 text-right tabular-nums',
                        short
                          ? 'font-medium text-red-700 dark:text-red-400'
                          : 'text-muted-foreground',
                      )}
                    >
                      {i.openDemand ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {i.reorderLevel ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {i.lastCountedQty ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mr-1 text-muted-foreground"
                        onClick={() => setHistorySlug(i.slug)}
                      >
                        History
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingSlug(i.slug)}
                        disabled={!canWrite}
                      >
                        Update
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {archivedCount > 0 && (
        <button
          type="button"
          className="text-xs text-muted-foreground underline underline-offset-4"
          onClick={() => setShowArchived((s) => !s)}
        >
          {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
        </button>
      )}

      <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
        <DialogContent className="sm:max-w-md">
          {createOpen && <CreateItemBody onClose={() => setCreateOpen(false)} />}
        </DialogContent>
      </Dialog>

      <Dialog open={historySlug !== null} onOpenChange={(o) => !o && setHistorySlug(null)}>
        <DialogContent className="max-h-[85vh] sm:max-w-lg">
          {historySlug && <ItemHistoryBody key={historySlug} slug={historySlug} />}
        </DialogContent>
      </Dialog>

      <Dialog open={editingItem !== null} onOpenChange={(o) => !o && setEditingSlug(null)}>
        <DialogContent className="max-h-[85vh] sm:max-w-lg">
          {editingItem && (
            <UpdateItemBody
              key={editingItem.slug}
              item={editingItem}
              onClose={() => setEditingSlug(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateItemBody({ onClose }: { onClose: () => void }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [reorder, setReorder] = useState('');

  const reorderN = Number(reorder.trim());
  const reorderValid = reorder.trim() === '' || (Number.isInteger(reorderN) && reorderN >= 0);

  const create = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST('/stock-items', {
        body: {
          name: name.trim(),
          reorderLevel: reorder.trim() === '' ? undefined : Math.trunc(reorderN),
        },
      });
      if (error || !data?.ok) throw new Error(error?.error?.detail ?? 'Could not create the item.');
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock-items'] });
      onClose();
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>New stock item</DialogTitle>
        <DialogDescription>
          Name the physical thing on the shelf (e.g. “Bumper decal 4in”) — not a store listing.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="item-name">Name</Label>
          <Input
            id="item-name"
            placeholder="Bumper decal 4in"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="item-reorder">Reorder level (optional)</Label>
          <Input
            id="item-reorder"
            type="number"
            inputMode="numeric"
            min="0"
            step="1"
            placeholder="e.g. 10"
            value={reorder}
            onChange={(e) => setReorder(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">Warn me when on hand drops to this.</p>
        </div>
        {create.isError && (
          <p className="text-xs text-destructive">
            {create.error instanceof Error ? create.error.message : 'Create failed.'}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!name.trim() || !reorderValid || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending && <Loader2 className="size-4 animate-spin" />}
            Create item
          </Button>
        </div>
      </div>
    </>
  );
}

type MoveMode = 'count' | 'add' | 'adjust';

function UpdateItemBody({ item, onClose }: { item: StockItemOnHand; onClose: () => void }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [mode, setMode] = useState<MoveMode>('count');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [reorder, setReorder] = useState(item.reorderLevel?.toString() ?? '');
  // One idempotency token per submit context, reused across a failed-then-retry so the server dedupes.
  const tokenRef = useRef<string | null>(null);
  const resetToken = () => {
    tokenRef.current = null;
  };

  const invalidate = () => qc.invalidateQueries({ queryKey: ['stock-items'] });

  const n = Number(qty.trim());
  const validQty =
    qty.trim() !== '' &&
    Number.isInteger(n) &&
    (mode === 'count' ? n >= 0 : mode === 'add' ? n > 0 : n !== 0);
  const nextOnHand = !validQty ? null : mode === 'count' ? n : item.onHand + n;

  const record = useMutation({
    mutationFn: async () => {
      if (!tokenRef.current) tokenRef.current = crypto.randomUUID();
      const token = tokenRef.current;
      const path = { slug: item.slug };
      if (mode === 'count') {
        const { data, error } = await client.POST('/stock-items/{slug}/count', {
          params: { path },
          body: {
            countedQty: Math.trunc(n),
            reason: reason.trim() || undefined,
            clientToken: token,
          },
        });
        if (error || !data?.ok)
          throw new Error(error?.error?.detail ?? 'Could not record the count.');
        return data.data;
      }
      if (mode === 'add') {
        const { data, error } = await client.POST('/stock-items/{slug}/add', {
          params: { path },
          body: { qty: Math.trunc(n), reason: reason.trim() || undefined, clientToken: token },
        });
        if (error || !data?.ok)
          throw new Error(error?.error?.detail ?? 'Could not record the restock.');
        return data.data;
      }
      const { data, error } = await client.POST('/stock-items/{slug}/adjust', {
        params: { path },
        body: { qtyDelta: Math.trunc(n), reason: reason.trim() || undefined, clientToken: token },
      });
      if (error || !data?.ok)
        throw new Error(error?.error?.detail ?? 'Could not record the adjustment.');
      return data.data;
    },
    onSuccess: () => {
      invalidate();
      resetToken();
      setQty('');
      setReason('');
    },
  });

  const reorderN = Number(reorder.trim());
  const reorderValid = reorder.trim() === '' || (Number.isInteger(reorderN) && reorderN >= 0);
  const reorderDirty = reorder.trim() !== (item.reorderLevel?.toString() ?? '');

  const saveSettings = useMutation({
    mutationFn: async (body: { reorderLevel?: number | null; archived?: boolean }) => {
      const { data, error } = await client.PATCH('/stock-items/{slug}', {
        params: { path: { slug: item.slug } },
        body,
      });
      if (error || !data?.ok) throw new Error(error?.error?.detail ?? 'Could not save.');
      return data.data;
    },
    onSuccess: () => invalidate(),
  });

  const modeLabel: Record<MoveMode, string> = {
    count: 'Count (set)',
    add: 'Restock (+)',
    adjust: 'Adjust (±)',
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{item.name}</DialogTitle>
        <DialogDescription>
          <span className="font-medium">Count</span> sets on hand to what's on the shelf.{' '}
          <span className="font-medium">Restock</span> records a delivery.{' '}
          <span className="font-medium">Adjust</span> fixes scrap, damage, or found items.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 rounded-md border p-3">
        <div className="flex items-center gap-1 rounded-md bg-muted p-0.5 text-xs font-medium">
          {(['count', 'add', 'adjust'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMode(m);
                record.reset();
                resetToken();
              }}
              className={cn(
                'flex-1 rounded px-2 py-1',
                mode === m ? 'bg-background shadow-sm' : 'text-muted-foreground',
              )}
            >
              {modeLabel[m]}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2 text-sm">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">On hand now</Label>
            <div className="tabular-nums">{item.onHand}</div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="stock-qty" className="text-xs text-muted-foreground">
              {mode === 'count' ? 'Set to' : mode === 'add' ? 'Units added' : 'Change by (±)'}
            </Label>
            <Input
              id="stock-qty"
              type="number"
              inputMode="numeric"
              step="1"
              min={mode === 'adjust' ? undefined : '0'}
              className="h-8"
              placeholder={mode === 'adjust' ? '+ / -' : undefined}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">New on hand</Label>
            <div className="tabular-nums font-medium">{nextOnHand ?? '—'}</div>
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="stock-reason" className="text-xs text-muted-foreground">
            Reason (optional)
          </Label>
          <Input
            id="stock-reason"
            placeholder={
              mode === 'count'
                ? 'physical count'
                : mode === 'add'
                  ? 'e.g. supplier delivery'
                  : 'e.g. damaged'
            }
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        <div className="flex items-center justify-end">
          <Button onClick={() => record.mutate()} disabled={!validQty || record.isPending}>
            {record.isPending && <Loader2 className="size-4 animate-spin" />}
            Save {mode === 'count' ? 'count' : mode === 'add' ? 'restock' : 'adjustment'}
          </Button>
        </div>

        {record.isError && (
          <p className="text-xs text-destructive">
            {record.error instanceof Error ? record.error.message : 'Save failed.'}
          </p>
        )}
        {record.isSuccess && (
          <p className="text-xs text-emerald-700 dark:text-emerald-400">
            Saved — on hand is now {record.data.onHand}.
          </p>
        )}
      </div>

      <div className="space-y-2 rounded-md border p-3">
        <Label htmlFor="stock-reorder" className="text-xs font-medium text-muted-foreground">
          Reorder level
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id="stock-reorder"
            type="number"
            inputMode="numeric"
            min="0"
            step="1"
            className="h-8 w-28"
            placeholder="none"
            value={reorder}
            onChange={(e) => setReorder(e.target.value)}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={!reorderValid || !reorderDirty || saveSettings.isPending}
            onClick={() =>
              saveSettings.mutate({
                reorderLevel: reorder.trim() === '' ? null : Math.trunc(reorderN),
              })
            }
          >
            Save level
          </Button>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            disabled={saveSettings.isPending}
            onClick={() => saveSettings.mutate({ archived: !item.archived })}
          >
            {item.archived ? 'Restore item' : 'Archive item'}
          </Button>
        </div>
        {saveSettings.isError && (
          <p className="text-xs text-destructive">
            {saveSettings.error instanceof Error ? saveSettings.error.message : 'Save failed.'}
          </p>
        )}
      </div>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onClose}>
          Done
        </Button>
      </div>
    </>
  );
}

/** The item's ledger history (production-records R0) — who moved it, when, and for which order. */
function ItemHistoryBody({ slug }: { slug: string }) {
  const client = useApiClient();
  const query = useQuery({
    queryKey: ['stock-items', 'detail', slug],
    queryFn: async () => {
      const { data, error } = await client.GET('/stock-items/{slug}', {
        params: { path: { slug } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the item history.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  return (
    <>
      <DialogHeader>
        <DialogTitle>History — {query.data?.item.name ?? slug}</DialogTitle>
      </DialogHeader>
      {query.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : query.isError ? (
        <p className="text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : 'Failed to load.'}
        </p>
      ) : (
        <MovementList
          rows={query.data.movements}
          emptyText="No history yet — record a count to start one."
        />
      )}
    </>
  );
}
